/* Halloween Mystery Sale (Oct 29-31, 2026)
 *
 * Drives the countdowns in the sale bar (layout/announcement-bar/mystery-sale)
 * and the pricing page header (page-content/pricing/header). The discount
 * changes several times a day, so the live offer always comes from the
 * no-store endpoint in [data-mystery-sale-endpoint]; pages showing the bar may
 * be served from the CDN cache, the pricing page is not.
 *
 * When a countdown reaches zero, the next offer is fetched and the percentages
 * and plan prices on the page are updated in place. Once the sale is over, the
 * bar hides itself, the homepage hero turns blue again and the pricing page
 * reloads to show regular pricing.
 */

if(typeof FN == 'undefined') FN = {};

FN.mysterySale = {
  config: {
    retryDelay: 5000,     // ms; the licensing API may lag a little at a boundary
    maxRetries: 24,
    revealDelay: 700      // ms; matches the announcement bar's slide-in
  },

  endpoint: null,
  endsAt: null,
  timer: null,
  retries: 0,


  init: function() {
    var source = document.querySelector('[data-mystery-sale-endpoint]');
    if(!source) return;

    FN.mysterySale.endpoint = source.getAttribute('data-mystery-sale-endpoint');
    FN.mysterySale.revealBar();

    // The pricing page renders the current end time; start right away with it
    var initialEndsAt = source.getAttribute('data-mystery-ends-at');
    if(initialEndsAt) FN.mysterySale.start(Date.parse(initialEndsAt));

    // Always sync with the live offer: this page may be a cached copy
    FN.mysterySale.refresh();
  },


  // Let the bar's moonlit glow spill over the page once it has slid in
  revealBar: function() {
    var bar = document.querySelector('.announcement-bar--mystery-sale');
    if(!bar) return;

    var reveal = function() {
      setTimeout(function() { bar.classList.add('announcement-bar--revealed'); }, FN.mysterySale.config.revealDelay);
    };
    if(document.readyState === 'complete') reveal();
    else window.addEventListener('load', reveal);
  },


  refresh: function() {
    if(!window.fetch) return;

    fetch(FN.mysterySale.endpoint, { cache: 'no-store', credentials: 'same-origin' })
      .then(function(response) {
        if(!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(FN.mysterySale.apply)
      .catch(FN.mysterySale.retryLater);
  },


  retryLater: function() {
    if(FN.mysterySale.retries >= FN.mysterySale.config.maxRetries) return;
    FN.mysterySale.retries++;
    setTimeout(FN.mysterySale.refresh, FN.mysterySale.config.retryDelay);
  },


  apply: function(offer) {
    // Sale over (or not started): hide the bar, show regular pricing
    if(!offer || !offer.running) {
      FN.mysterySale.end();
      return;
    }

    var endsAt = offer.active ? Date.parse(offer.endsAt) : NaN;

    // Between two discounts, or the API hasn't switched to the next one yet
    if(isNaN(endsAt) || endsAt <= Date.now()) {
      FN.mysterySale.retryLater();
      return;
    }

    FN.mysterySale.retries = 0;
    FN.mysterySale.updatePercentages(offer.percentOff);
    FN.mysterySale.updatePlanPrices(offer.plans || {});
    FN.mysterySale.start(endsAt);
  },


  start: function(endsAt) {
    if(isNaN(endsAt)) return;

    FN.mysterySale.endsAt = endsAt;
    clearInterval(FN.mysterySale.timer);
    FN.mysterySale.tick();
    FN.mysterySale.timer = setInterval(FN.mysterySale.tick, 1000);

    var countdowns = document.querySelectorAll('[data-mystery-countdown]');
    for(var i = 0; i < countdowns.length; i++) countdowns[i].removeAttribute('hidden');
  },


  tick: function() {
    var remaining = Math.max(0, Math.round((FN.mysterySale.endsAt - Date.now()) / 1000));
    FN.mysterySale.render(remaining);

    if(remaining <= 0) {
      clearInterval(FN.mysterySale.timer);
      setTimeout(FN.mysterySale.refresh, 1000);
    }
  },


  render: function(seconds) {
    var hours = Math.floor(seconds / 3600);
    var minutes = Math.floor((seconds % 3600) / 60);
    var secs = seconds % 60;
    var pad = function(n) { return (n < 10 ? '0' : '') + n; };

    var groups = hours > 0 ? [String(hours), pad(minutes), pad(secs)] : [pad(minutes), pad(secs)];
    var label = (hours > 0 ? hours + ' hours ' : '') + minutes + ' minutes ' + secs + ' seconds';

    var values = document.querySelectorAll('[data-mystery-countdown-value]');
    for(var i = 0; i < values.length; i++) {
      var el = values[i];
      var tiles = el.getAttribute('data-mystery-format') === 'tiles';
      var html = '';

      for(var g = 0; g < groups.length; g++) {
        if(g > 0) html += '<span class="mystery-countdown__sep">:</span>';
        if(tiles) {
          for(var d = 0; d < groups[g].length; d++) html += '<span class="mystery-countdown__tile">' + groups[g].charAt(d) + '</span>';
        } else {
          html += '<span class="mystery-countdown__unit">' + groups[g] + '</span>';
        }
      }

      el.innerHTML = html;
      el.setAttribute('aria-label', label);
    }
  },


  updatePercentages: function(percentOff) {
    if(percentOff === null || percentOff === undefined) return;

    var els = document.querySelectorAll('[data-mystery-percent]');
    for(var i = 0; i < els.length; i++) els[i].textContent = percentOff;
  },


  // Plan cards on the pricing page: <div data-mystery-plan="tower-pro">
  updatePlanPrices: function(plans) {
    var cards = document.querySelectorAll('[data-mystery-plan]');

    for(var i = 0; i < cards.length; i++) {
      var plan = plans[cards[i].getAttribute('data-mystery-plan')];
      if(!plan || !plan.discountedPrice) continue;

      FN.mysterySale.setPrice(cards[i].querySelector('.price__regular'), plan.currencySymbol, plan.regularPrice);
      FN.mysterySale.setPrice(cards[i].querySelector('.annual-price'), plan.currencySymbol, plan.discountedPrice);
      FN.mysterySale.setPrice(cards[i].querySelector('.monthly-price'), plan.currencySymbol, plan.discountedPrice);

      var percent = cards[i].querySelector('[data-mystery-percent]');
      if(percent && plan.percentOff !== null) percent.textContent = plan.percentOff;
    }
  },


  setPrice: function(priceEl, currencySymbol, amount) {
    if(!priceEl) return;

    var currencyEl = priceEl.querySelector('.price__currency');
    var amountEl = priceEl.querySelector('.price__amount');
    if(currencyEl && currencySymbol) currencyEl.textContent = currencySymbol;
    if(amountEl && amount !== null && amount !== undefined) amountEl.textContent = amount;
  },


  end: function() {
    clearInterval(FN.mysterySale.timer);

    var bar = document.querySelector('.announcement-bar--mystery-sale');
    if(bar) bar.setAttribute('hidden', '');

    // The homepage hero goes back to its usual blue (a cached copy of the page
    // may still have been rendered during the sale)
    var hero = document.querySelector('.hero--mystery-sale');
    if(hero) {
      hero.classList.remove('hero--mystery-sale');
      var moon = hero.querySelector('.hero-mystery-moon');
      if(moon) moon.remove();
    }

    // The pricing page rendered sale prices; get regular pricing instead
    if(document.querySelector('[data-mystery-reload-on-end]')) window.location.reload();
  }
};


document.addEventListener('DOMContentLoaded', FN.mysterySale.init);
